import React, { Component } from 'react';

import { compose } from 'redux';

import { loadMoreNotifications, reloadNotifications } from 'common/actions/notifications';
import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import NotificationList from 'common/notifications/NotificationList';
import SpinnerV2 from 'common/SpinnerV2';
import AdminNotificationsSidebar from 'common/subdomain/admin/AdminNotificationsSidebar';
import Tappable from 'common/Tappable';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import withContexts from 'common/util/withContexts';

import type { Board } from 'common/api/endpoints/boards';
import type { Company } from 'common/api/endpoints/companies';
import type { Viewer } from 'common/api/endpoints/viewer';
import type { NotificationsState } from 'common/reducers/notifications';

import 'css/components/subdomain/admin/_AdminNotifications.scss';

type OwnProps = {
  boards: Board[];
  company: Company;
  viewer: Viewer;
};

type ConnectProps = {
  notifications: NotificationsState;
  loadMoreNotifications: () => void;
  reloadNotifications: () => void;
};

type Props = OwnProps & ConnectProps;

class AdminNotifications extends Component<Props> {
  private notificationListRef = React.createRef<NotificationList>();
  private scrollContainerRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.scrollContainerRef.current?.addEventListener('scroll', this.onScroll);
    this.onScroll();
    this.handleMarkAllSeen();
  }

  componentWillUnmount() {
    this.scrollContainerRef.current?.removeEventListener('scroll', this.onScroll);
  }

  handleMarkAllSeen = () => {
    const { viewer } = this.props;
    if (!viewer?._id) {
      return;
    }

    return AJAX.post('/api/notifications/markAllSeen');
  };

  onLoadMoreTapped = () => {
    this.props.loadMoreNotifications();
  };

  markAllAsRead = async () => {
    const response = await AJAX.post('/api/notifications/markAllRead');
    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      return;
    }

    this.props.reloadNotifications();
  };

  onScroll = () => {
    const notificationList = this.notificationListRef.current;
    if (!notificationList) {
      return;
    }

    const scrollContainer = this.scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    notificationList.onScroll(scrollContainer);
  };

  renderMarkAllLink() {
    return (
      <div className="markAll">
        {this.props.notifications?.reloading ? <SpinnerV2 size="small" /> : null}
        <Tappable onTap={this.markAllAsRead}>
          <div className="markAllButton">Mark all as read</div>
        </Tappable>
      </div>
    );
  }

  renderContents() {
    const { viewer } = this.props;
    if (!viewer?._id) {
      return <div className="loggedOut">You are not logged in.</div>;
    }

    return (
      <>
        <div className="notificationHeader">
          <div className="heading">Notifications</div>
          {this.renderMarkAllLink()}
        </div>
        <NotificationList
          notifications={this.props.notifications}
          onLoadMoreTapped={this.onLoadMoreTapped}
          ref={this.notificationListRef}
        />
      </>
    );
  }

  render() {
    return (
      <div className="adminNotifications">
        <Helmet title={'Notifications | Canny'} />
        <AdminNotificationsSidebar boards={this.props.boards} company={this.props.company} />
        <div className="adminNotificationsContents" ref={this.scrollContainerRef}>
          {this.renderContents()}
        </div>
      </div>
    );
  }
}

export default compose(
  connect((state: any) => ({ notifications: state.notifications }), {
    loadMoreNotifications,
    reloadNotifications,
  }),
  withContexts(
    {
      company: CompanyContext,
      viewer: ViewerContext,
    },
    {
      forwardRef: true,
    },
  ),
)(AdminNotifications) as unknown as React.ComponentType<Omit<Props, keyof ConnectProps>>;
